/**
 * @flow
 */

import React from 'react';
import styled from 'styled-components';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { defineMessages, injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { format } from 'date-fns';
import { get, filter, uniqBy } from 'lodash';

import { Input, Form } from 'semantic-ui-react';
import { FormLabel } from '../../../../components';
import BaseCalendar from '../../containers/form/Calendar';
import {
    ClientSelect,
} from '../../containers/form';

import CabinetServiceFields from '../../containers/form/CabinetServiceFields';
import MasterServiceFields from '../../containers/form/MasterServiceFields';

import CabinetServiceFieldList from './CabinetServiceFieldList';
import MasterServiceFieldList from './MasterServiceFieldList';

import styles from '../../../../styles';
import { MASTER_APPOINTMENT_FORM_FRAGMENT } from '../../containers/masterAccessQueries';

import type { Appointment, Intl } from '../../../../type';

const GET_MASTERS_QUERY = gql`
    query getMasters(
        $salon: ID!
        $date: String!
        $master: ID!
        $withMaster: Boolean!
        $lang: String!
        $withCabinets: Boolean!
        $showArchive: Boolean!
        $appointment: String
        $service: String
    ) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    settings {
                        schedule {
                            startAt
                            endAt
                            step
                            format
                        }
                    }
                    masters(deleted: true) @skip(if: $withCabinets) {
                        nodes {
                            id
                            ...master
                            freeWorkingTime(
                                date: $date
                                appointment: $appointment
                                showArchive: $showArchive
                            ) {
                                startAt
                                endAt
                            }
                            serviceGroups {
                                id
                                ...serviceGroup
                                cabinets {
                                    id
                                    ...cabinet
                                    freeWorkingTime(
                                        date: $date
                                        appointment: $appointment
                                        showArchive: $showArchive
                                    ) {
                                        startAt
                                        endAt
                                    }
                                }
                            }
                        }
                    }
                    master(id: $master) @include(if: $withMaster) {
                        id
                        ...master
                        freeWorkingTime(
                            date: $date
                            appointment: $appointment
                            showArchive: $showArchive
                        ) {
                            startAt
                            endAt
                        }
                        serviceGroups(service: $service) {
                            id
                            ...serviceGroup
                            cabinets {
                                id
                                ...cabinet
                                freeWorkingTime(
                                    date: $date
                                    appointment: $appointment
                                    showArchive: $showArchive
                                ) {
                                    startAt
                                    endAt
                                }
                            }
                        }
                    }
                    cabinets @include(if: $withCabinets) {
                        id
                        ...cabinet
                        serviceGroups(service: $service) {
                            id
                            ...serviceGroup
                            masters {
                                id
                                ...master
                            }
                        }
                    }
                }
            }
            ...onMasterAppointmentForm
        }
    }

    fragment cabinet on Cabinet {
        name(lang: $lang)
        schedule(startDate: $date) {
            startAt
            endAt
        }
    }

    fragment master on Master {
        firstName
        lastName
        deletedAt
        schedule(startDate: $date) {
            id
            startAt
            endAt
        }
    }

    fragment serviceGroup on ServiceGroup {
        name(lang: $lang)
        services {
            id
            name(lang: $lang)
            duration
            price
        }
    }

    ${MASTER_APPOINTMENT_FORM_FRAGMENT}
`;

const intlMessages = {
    date: defineMessages({
        placeholder: {
            id: 'pages.schedule.form.AppointmentForm.datePlaceholder',
            defaultMessage: 'Select an appointment date',
        },
        label: {
            id: 'pages.schedule.form.AppointmentForm.dateLabel',
            defaultMessage: 'Date',
        },
    }),
    client: defineMessages({
        placeholder: {
            id: 'pages.schedule.form.AppointmentForm.clientPlaceholder',
            defaultMessage: 'Select a client',
        },
        label: {
            id: 'pages.schedule.form.AppointmentForm.clientLabel',
            defaultMessage: 'Client',
        },
    }),
    notes: defineMessages({
        placeholder: {
            id: 'pages.schedule.form.AppointmentForm.notesPlaceholder',
            defaultMessage: 'Write a note (optional)',
        },
        label: {
            id: 'pages.schedule.form.AppointmentForm.notesLabel',
            defaultMessage: 'Notes',
        },
    }),
};

type AppointmentFormProps = {
    appointment: ?Appointment,
    intl: Intl,
    updateAppointmentClient: Function,
    onCloseDialog: Function,
    inCabinet: boolean,
    disabled: boolean,
};

const Label = styled(FormLabel)`
    ${styles.proximaNovaFontFamily} !important;
`;

const Calendar = styled(BaseCalendar)`
    & .DayPickerInput-OverlayWrapper {
        top: 46px;
    }
`;

const SecondRow = styled.div`
    padding-top: 15px;
`;

function AppointmentForm({
    appointment,
    intl,
    updateAppointmentClient,
    onCloseDialog,
    inCabinet,
    disabled,
}: AppointmentFormProps) {
    const { control, setValue } = useFormContext();

    const salonId = useSelector(({ user }) => user.get('salon'));
    const currentLanguage = useSelector(
        ({ intl: i }) => i.get('locale') || i.get('defaultLanguage'),
    );
    const showArchive = useSelector(({ salon: s }) => s.get('showArchive'));

    const startDate = useWatch({ control, name: 'startDate' });
    const masterId = useWatch({ control, name: 'master' });

    const { data, loading } = useQuery(GET_MASTERS_QUERY, {
        variables: {
            salon: salonId,
            // format() gives the local calendar day; toISOString() is UTC and
            // yields the wrong day near midnight in non-UTC timezones.
            date: startDate || format(new Date(), 'yyyy-MM-dd'),
            master: masterId || '',
            withMaster: !!masterId,
            lang: currentLanguage,
            withCabinets: inCabinet || false,
            showArchive,
            appointment: appointment ? appointment.id : undefined,
            service: appointment ? appointment.service.id : undefined,
        },
        fetchPolicy: 'cache-and-network',
        skip: !salonId || !appointment,
    });

    const masters =
        !loading && data && data.viewer
            ? uniqBy(
                  filter(
                      [
                          get(data, 'viewer.salon.master', {}),
                          ...get(data, 'viewer.salon.masters.nodes', []),
                      ],
                      ({ id, schedule }) => {
                          let result = schedule && schedule.length;
                          if (!result && id === masterId) {
                              result = true;
                          }
                          return result;
                      },
                  ),
                  'id',
              )
            : [];

    const cabinets =
        !loading && data && data.viewer
            ? get(data, 'viewer.salon.cabinets', [])
            : [];

    return (
        <Form>
            <Form.Group>
                <Controller
                    name="client"
                    control={control}
                    render={({ field: { value, onChange }, fieldState }) => (
                        <Form.Field width={12} required error={!!fieldState.error}>
                            <Label>
                                {intl.formatMessage(intlMessages.client.label)}
                            </Label>
                            <ClientSelect
                                value={value}
                                onChange={(e, { value: v }) => onChange(v)}
                                name="client"
                                change={setValue}
                                placeholder={intl.formatMessage(intlMessages.client.placeholder)}
                                updateAppointmentClient={updateAppointmentClient}
                                search
                                newAppointmentUseCase={false}
                                onCloseDialog={onCloseDialog}
                            />
                        </Form.Field>
                    )}
                />
                <Controller
                    name="startDate"
                    control={control}
                    render={({ field: { value, onChange }, fieldState }) => (
                        <Form.Field width={4} required error={!!fieldState.error}>
                            <Label>{intl.formatMessage(intlMessages.date.label)}</Label>
                            <Calendar
                                value={value}
                                onChange={onChange}
                                format="dd.MM.yyyy"
                                placeholder={intl.formatMessage(intlMessages.date.placeholder)}
                                dayPickerProps={{
                                    numberOfMonths: 2,
                                    fixedWeeks: true,
                                }}
                                disabled={!!(appointment && appointment.group)}
                            />
                        </Form.Field>
                    )}
                />
            </Form.Group>
            <Controller
                name="notes"
                control={control}
                render={({ field: { value, onChange, onBlur, name, ref } }) => (
                    <Form.Field>
                        <Label>{intl.formatMessage(intlMessages.notes.label)}</Label>
                        <Input
                            value={value || ''}
                            onChange={e => onChange(e.target.value)}
                            onBlur={onBlur}
                            name={name}
                            ref={ref}
                            placeholder={intl.formatMessage(intlMessages.notes.placeholder)}
                        />
                    </Form.Field>
                )}
            />
            {inCabinet ? (
                <div>
                    <CabinetServiceFields
                        cabinets={cabinets}
                        withLabel
                        loading={loading}
                    />
                    {appointment && !appointment.invoice && (
                        <SecondRow>
                            <CabinetServiceFieldList
                                withLabel={false}
                                multiple
                            />
                        </SecondRow>
                    )}
                </div>
            ) : (
                <div>
                    <MasterServiceFields
                        masters={masters}
                        withLabel
                        loading={loading}
                    />
                    {appointment && !appointment.invoice && (
                        <SecondRow>
                            <MasterServiceFieldList
                                withLabel={false}
                                multiple
                            />
                        </SecondRow>
                    )}
                </div>
            )}
        </Form>
    );
}

export default injectIntl(AppointmentForm);
